/**
 * @file bbd-datalayer-cadeau.js
 * @description GA4 tracking for Monk GWP (gift with purchase) popup events.
 *
 * The GWP gift is chosen in our gift picker (assets/bbd-gift-picker.js, PIG-320), which falls back to the Monk app
 * when its funnel is unavailable. The Monk widget renders inside:
 *   <div id="mr-div-embedded-cp-freegift_manual-drawer">
 *
 * The GWP accordion uses <accordion-custom class="cart-gwp accordion"> (without
 * the cart-crosssells class). The cross-sell accordion uses
 * <accordion-custom class="cart-gwp cart-crosssells accordion">.
 * We use :not(.cart-crosssells) to target only the GWP accordion.
 *
 * Cart context:
 * - 'drawer' — mini cart dialog (<cart-drawer-component> / .cart-drawer__dialog[open])
 * - 'page'   — standalone cart page (#bbd-datalayer-cart / .template-cart body class)
 *
 * Hooks:
 * - GWP accordion <details> toggle → popup_cadeau_show / popup_cadeau_open / popup_cadeau_close
 * - bbd-gift:synced (our gift picker, a gift line that was not in the cart before) and the cart:refresh custom
 *   event (fired by Monk after gift selection) → cadeau_to_cart / cart_cadeau_tocart
 * - Size <select> change inside Monk widget → cadeau_size_change
 *
 * cadeau_verpakking_open / cadeau_verpakking_select are tracked via a Customer Events
 * pixel in Shopify Admin, fired by the checkout UI extension — not in this file.
 *
 * Monk GWP cart item identification:
 * Monk sets the line item property '_mr' on gift items (see isGiftLine in assets/bbd-cart-rules.js).
 * We use this to distinguish genuine gift items from 0-price items created by
 * RMA flows or other discount mechanisms.
 */

import { isGiftLine } from '@bbd/cart-rules';

const push = (eventObj) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventObj);
};

// ---------------------------------------------------------------------------
// Cart context detection
//
// Used by all cadeau events to include a placement field that distinguishes
// interactions in the mini cart drawer from those on the cart page.
//
// For DOM-based events (toggle, change), pass the element that triggered the
// event so we can check if it is inside the drawer.
// For non-DOM events (cart:refresh), we fall back to checking if the drawer
// dialog is currently open.
// ---------------------------------------------------------------------------

/**
 * Determines whether the user is interacting via the mini cart drawer or the
 * standalone cart page.
 *
 * @param {Element|null} [el] - Optional DOM element to check ancestry for.
 *   When provided, checks if the element is inside the cart drawer component.
 *   When omitted, checks if the cart drawer dialog is currently open.
 * @returns {'drawer'|'page'}
 */
const getCartContext = (el = null) => {
  if (el) {
    return el.closest('cart-drawer-component') ? 'drawer' : 'page';
  }
  // For non-DOM events (e.g. cart:refresh), check if the drawer is open
  const drawerOpen = !!document.querySelector('.cart-drawer__dialog[open]');
  if (drawerOpen) return 'drawer';
  const isCartPage =
    document.body.classList.contains('template-cart') || !!document.getElementById('bbd-datalayer-cart');
  return isCartPage ? 'page' : 'drawer'; // default to drawer when ambiguous (most common context)
};

// ---------------------------------------------------------------------------
// GWP accordion open/close tracking
// Uses :not(.cart-crosssells) to avoid firing on the cross-sell accordion
// which also has the cart-gwp class.
// ---------------------------------------------------------------------------
const initGwpPopupTracking = () => {
  document.addEventListener(
    'toggle',
    (e) => {
      const details = e.target.closest('details');
      if (!details) return;

      // Only track the GWP accordion, not the cross-sells accordion
      const accordion = details.closest('accordion-custom.cart-gwp:not(.cart-crosssells)');
      if (!accordion) return;

      const placement = getCartContext(accordion);

      if (details.open) {
        const itemName = accordion.querySelector('.cart-gwp__label')?.textContent.trim() ?? '';

        push({
          event: 'popup_cadeau_show',
          item_name: itemName,
          placement
        });

        push({
          event: 'popup_cadeau_open',
          item_name: itemName,
          placement
        });
      } else {
        push({
          event: 'popup_cadeau_close',
          placement
        });
      }
    },
    true
  ); // capture phase to catch toggle on details
};

// ---------------------------------------------------------------------------
// Gift added to cart — listen to cart:refresh event fired by Monk after selection
//
// The event name differs depending on context:
// - 'cadeau_to_cart'      — gift added from the mini cart drawer (most common)
// - 'cart_cadeau_tocart'  — gift added from the standalone cart page
// ---------------------------------------------------------------------------

const cadeauState = { lastCartItems: null };

const snapshotCart = async () => {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    cadeauState.lastCartItems = cart.items;
  } catch {
    // silently fail
  }
};

// ---------------------------------------------------------------------------
// Gift added with our gift picker. The picker dispatches bbd-gift:synced after every cart update with the key of the
// gift line, a key we have not seen before means a gift was added or changed. The first event only sets the baseline.
// ---------------------------------------------------------------------------
const initGiftPickerTracking = () => {
  /** @type {string | null | undefined} */
  let lastGiftKey;

  document.addEventListener('bbd-gift:synced', (event) => {
    const { giftKey, giftTitle, items } = event.detail;
    const isNewGift = lastGiftKey !== undefined && giftKey !== null && giftKey !== lastGiftKey;
    lastGiftKey = giftKey;

    // Keep the snapshot of the Monk path in step, so a later cart:refresh does not report this gift again
    cadeauState.lastCartItems = items;

    if (!isNewGift) return;

    const placement = getCartContext();
    push({
      event: placement === 'page' ? 'cart_cadeau_tocart' : 'cadeau_to_cart',
      item_name: giftTitle,
      placement
    });
  });
};

const initGwpCartRefreshTracking = () => {
  snapshotCart();

  document.addEventListener('cart:refresh', async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      const prevKeys = new Set((cadeauState.lastCartItems ?? []).map((i) => i.key));
      const newGiftItems = cart.items.filter((item) => !prevKeys.has(item.key) && isGiftLine(item));

      if (newGiftItems.length) {
        const itemName = newGiftItems[0].product_title ?? '';
        // No DOM element available here — getCartContext() falls back to checking
        // whether the drawer dialog is open, which is reliable since cart:refresh
        // fires while the user is still interacting with the cart.
        const placement = getCartContext();
        const isCartPage = placement === 'page';

        push({
          event: isCartPage ? 'cart_cadeau_tocart' : 'cadeau_to_cart',
          item_name: itemName,
          placement
        });
      }

      cadeauState.lastCartItems = cart.items;
    } catch {
      // silently fail
    }
  });
};

// ---------------------------------------------------------------------------
// GWP size/variant change — Monk renders a <select class="mr-input-select">
// per product tile inside the GWP widget. We listen via event delegation on
// the Monk container.
//
// The product title is read from .mr-product-details__product-title inside the
// nearest .mr-product-item ancestor of the changed select.
//
// Note: color selection is handled by Monk's carousel navigation (different
// product tiles), not a select element, so no separate color change event fires.
// ---------------------------------------------------------------------------
const initGwpSizeChangeTracking = () => {
  document.addEventListener('change', (e) => {
    const select = e.target.closest('#mr-div-embedded-cp-freegift_manual-drawer .mr-input-select');
    if (!select) return;

    const size = select.value;
    const productItem = select.closest('.mr-product-item');
    const itemName = productItem?.querySelector('.mr-product-details__product-title')?.textContent.trim() ?? '';
    const placement = getCartContext(select);

    push({
      event: 'cadeau_size_change',
      item_name: itemName,
      variant: size,
      placement
    });
  });
};

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  initGwpPopupTracking();
  initGiftPickerTracking();
  initGwpCartRefreshTracking();
  initGwpSizeChangeTracking();
});
